import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWorkspaceTabNavigation, useWorkspaceTabs } from '../context/WorkspaceTabsContext';
import financeReconciliationService from '../services/financeReconciliationService';
import { useAppDialog } from '../components/AppDialog';
import FloatingBannerStack from '../components/FloatingBannerStack';
import Modal from '../components/Modal';
import { AlertIcon, ArrowLeftIcon, CheckCircleIcon, CopyIcon, EditIcon, ExternalLinkIcon, SearchIcon, UploadIcon } from '../components/Icons';
import { notifyFinanceReconciliationUpdated } from '../utils/financeReconciliationEvents';
import useFinanceReconciliationAutoSave from '../hooks/useFinanceReconciliationAutoSave';
import { exportFinanceReport } from '../utils/financeReportExport';
import './FinanceReconciliationDetail.css';

const labelStyle = {
  display: 'block',
  marginBottom: '0.4rem',
  fontSize: '0.8125rem',
  fontWeight: 600,
  color: 'var(--text-secondary)'
};

const tableHeaderStyle = {
  padding: '0.75rem 1rem',
  textAlign: 'left',
  fontSize: '0.8rem',
  fontWeight: 700,
  color: 'var(--text-secondary)',
  whiteSpace: 'nowrap'
};

const tableCellStyle = {
  padding: '0.8rem 1rem',
  fontSize: '0.8125rem',
  verticalAlign: 'top',
  borderBottom: '1px solid var(--border-light)'
};

const detailColumnStyle = {
  width: '360px',
  minWidth: '360px',
  maxWidth: '360px',
  boxSizing: 'border-box'
};

const summaryGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
  gap: '0.75rem',
  marginBottom: '1rem'
};

const summaryCardStyle = {
  padding: '0.9rem',
  minWidth: 0
};

const summaryLabelStyle = {
  fontSize: '0.75rem',
  fontWeight: 700,
  color: 'var(--text-muted)',
  marginBottom: '0.25rem',
  lineHeight: 1.25
};

const summaryValueStyle = {
  fontSize: '1.1rem',
  fontWeight: 800,
  overflowWrap: 'anywhere'
};

const summaryCountValueStyle = {
  ...summaryValueStyle,
  fontSize: '1.35rem'
};

const amountFormatter = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const roundAmountToCents = (value) => {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount)) return 0;
  const normalized = Number(amount.toFixed(10));
  return Math.sign(normalized) * (Math.round(Math.abs(normalized) * 100) / 100);
};

const formatDate = (value) => {
  if (!value) return '-';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toISOString().slice(0, 10);
};

const getDateInputValue = (value) => {
  const date = formatDate(value);
  return date === '-' ? '' : date;
};

const getMonthStartDate = (reportMonth) => (
  /^\d{4}-(0[1-9]|1[0-2])$/.test(String(reportMonth || ''))
    ? `${reportMonth}-01`
    : ''
);

const getMonthEndDate = (reportMonth) => {
  const month = String(reportMonth || '');
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(month)) return '';

  const [year, monthNumber] = month.split('-').map(Number);
  const lastDay = new Date(Date.UTC(year, monthNumber, 0)).getUTCDate();
  return `${month}-${String(lastDay).padStart(2, '0')}`;
};

const createReportMetaForm = (report) => ({
  title: report?.title || '',
  report_month: report?.report_month || '',
  notes: report?.notes || '',
  include_ownline: report?.include_ownline !== false,
  include_free_wallet: report?.include_free_wallet === true
});

const formatAmount = (value, currencyCode = '') => {
  const amount = Number(value || 0);
  return `${currencyCode ? `${currencyCode} ` : ''}${amountFormatter.format(amount)}`;
};

const formatRate = (value) => `${Number(value || 0).toFixed(2)}%`;

const toAmountNumber = (value) => {
  if (value === '' || value === null || value === undefined) return 0;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getVarianceStyle = (value) => {
  const amount = roundAmountToCents(value);
  if (amount > 0) return { color: 'var(--success)' };
  if (amount < 0) return { color: 'var(--error)' };
  return { color: 'var(--text-secondary)' };
};

const EDITABLE_REPORT_STATUSES = ['draft', 'rejected'];

const STATUS_LABELS = {
  draft: 'Draft',
  submitted: 'Submitted',
  approved: 'Approved',
  rejected: 'Rejected',
  deleted: 'Deleted'
};

const VERIFICATION_FILTER_LABELS = {
  all: 'All',
  verified: 'Verified',
  unverified: 'Unverified'
};

const DIFFERENCE_FILTER_LABELS = {
  all: 'All',
  zero: 'Zero',
  non_zero: 'Non-zero'
};

const isRowVerified = (row) => !!row?.verified_at;

const isZeroDifference = (row) => roundAmountToCents(getVarianceAmount(row)) === 0;

const getStatusStyle = (status) => {
  if (status === 'deleted') return { color: 'var(--error)', backgroundColor: 'rgba(239, 68, 68, 0.1)' };
  if (status === 'approved') return { color: 'var(--success)', backgroundColor: 'rgba(16, 185, 129, 0.1)' };
  if (status === 'submitted') return { color: 'var(--primary)', backgroundColor: 'rgba(var(--primary-rgb), 0.1)' };
  if (status === 'rejected') return { color: 'var(--error)', backgroundColor: 'rgba(239, 68, 68, 0.1)' };
  return { color: 'var(--text-secondary)', backgroundColor: 'var(--bg-soft)' };
};

const formatAdminName = (value) => value?.username || value?.email || '-';

const getObjectIdString = (value) => String(value?._id || value?.id || value || '');

const isSameObjectId = (firstValue, secondValue) => {
  const firstId = getObjectIdString(firstValue);
  const secondId = getObjectIdString(secondValue);
  return !!firstId && firstId === secondId;
};

const formatWinIndicator = (value) => {
  if (value === 'positive') return 'Positive';
  if (value === 'negative') return 'Negative';
  return '-';
};

const getIndicatorStyle = (value) => {
  if (value === 'positive') return { color: 'var(--success)', backgroundColor: 'rgba(16, 185, 129, 0.1)' };
  if (value === 'negative') return { color: 'var(--error)', backgroundColor: 'rgba(239, 68, 68, 0.1)' };
  return { color: 'var(--text-secondary)', backgroundColor: 'var(--bg-soft)' };
};

const indicatorChipStyle = (value) => ({
  display: 'inline-flex',
  alignItems: 'center',
  minHeight: '22px',
  padding: '0.15rem 0.45rem',
  borderRadius: '4px',
  fontSize: '0.72rem',
  fontWeight: 800,
  ...getIndicatorStyle(value)
});

const formatExternalLink = (value) => {
  const link = String(value || '').trim();
  if (!link) return '';
  return /^https?:\/\//i.test(link) ? link : `https://${link}`;
};

const StackedLabelValue = ({ label, value, valueStyle = {}, children = null, onCopy = null }) => (
  <div style={{ display: 'grid', gap: '0.12rem', minWidth: 0 }}>
    <span style={{ color: 'var(--text-muted)', fontSize: '0.72rem', fontWeight: 700 }}>{label}</span>
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', minWidth: 0 }}>
      {children || (
        <strong style={{ minWidth: 0, color: 'var(--text-primary)', fontSize: '0.8rem', wordBreak: 'break-word', ...valueStyle }}>
          {value || '-'}
        </strong>
      )}
      {onCopy && value && value !== '-' && (
        <button
          type="button"
          onClick={() => onCopy(value, label)}
          title={`Copy ${label}`}
          style={{
            flex: '0 0 auto',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '22px',
            height: '22px',
            padding: 0,
            border: '1px solid var(--border-light)',
            borderRadius: '4px',
            backgroundColor: 'var(--bg-card)',
            color: 'var(--primary)',
            cursor: 'pointer'
          }}
        >
          <CopyIcon size={12} />
        </button>
      )}
    </span>
  </div>
);

const externalLinkButtonStyle = {
  flex: '0 0 auto',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '22px',
  height: '22px',
  padding: 0,
  border: '1px solid var(--border-light)',
  borderRadius: '4px',
  backgroundColor: 'var(--bg-card)',
  color: 'var(--primary)',
  cursor: 'pointer'
};

const CompactCredentialItem = ({ label, value, valueStyle = {}, children = null, onCopy = null, full = false }) => (
  <div style={{
    display: 'grid',
    gap: '0.12rem',
    minWidth: 0,
    gridColumn: full ? '1 / -1' : undefined
  }}>
    <span style={{ color: 'var(--text-muted)', fontSize: '0.7rem', fontWeight: 700 }}>{label}</span>
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', minWidth: 0 }}>
      {children || (
        <strong style={{ minWidth: 0, color: 'var(--text-primary)', fontSize: '0.76rem', wordBreak: 'break-word', ...valueStyle }}>
          {value || '-'}
        </strong>
      )}
      {onCopy && value && value !== '-' && (
        <button
          type="button"
          onClick={() => onCopy(value, label)}
          title={`Copy ${label}`}
          style={{
            flex: '0 0 auto',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '20px',
            height: '20px',
            padding: 0,
            border: '1px solid var(--border-light)',
            borderRadius: '4px',
            backgroundColor: 'var(--bg-card)',
            color: 'var(--primary)',
            cursor: 'pointer'
          }}
        >
          <CopyIcon size={11} />
        </button>
      )}
    </span>
  </div>
);

const BoCredentialsCell = ({ credentials, providerBoLink = '', providerRemarks = '', onCopy }) => {
  const environments = Array.isArray(credentials) ? credentials : [];
  const urlSeen = new Set();
  const boUrls = [];
  const providerLink = String(providerBoLink || '').trim();
  const remarks = String(providerRemarks || '').trim();
  const fieldsByKey = new Map();
  const remarkValues = [];
  const remarkSeen = new Set();

  const addRemark = (value) => {
    const text = String(value || '').trim();
    if (!text || remarkSeen.has(text)) return;
    remarkSeen.add(text);
    remarkValues.push(text);
  };

  addRemark(remarks);

  environments.forEach(env => {
    (env.bo_urls || []).forEach(url => {
      const value = String(url || '').trim();
      if (!value || urlSeen.has(value)) return;
      urlSeen.add(value);
      boUrls.push(value);
    });

    (env.fields || []).forEach(field => {
      const key = String(field.key || '').trim();
      if (!key) return;

      const value = String(field.value || '').trim();
      if (/^bo\s+remarks?$/i.test(key) || /^remarks?$/i.test(key)) {
        addRemark(value);
        return;
      }

      const existing = fieldsByKey.get(key);
      if (!existing || (!existing.value && value)) {
        fieldsByKey.set(key, { key, value });
      }
    });
  });

  const fields = Array.from(fieldsByKey.values());

  if (!providerLink && remarkValues.length === 0 && boUrls.length === 0 && fields.length === 0) {
    return <span style={{ color: 'var(--text-muted)' }}>-</span>;
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.45rem 0.75rem', minWidth: 0 }}>
      {providerLink && (
        <CompactCredentialItem
          label="Provider BO Link"
          value={providerLink}
        >
          <a
            href={formatExternalLink(providerLink)}
            target="_blank"
            rel="noreferrer"
            style={{ minWidth: 0, color: 'var(--primary)', fontSize: '0.76rem', fontWeight: 800, wordBreak: 'break-word', textDecoration: 'none' }}
          >
            {providerLink}
          </a>
          <a
            href={formatExternalLink(providerLink)}
            target="_blank"
            rel="noreferrer"
            title="Open Provider BO Link"
            style={externalLinkButtonStyle}
          >
            <ExternalLinkIcon size={12} />
          </a>
        </CompactCredentialItem>
      )}
      {boUrls.map((url, index) => (
        <CompactCredentialItem
          key={`bo-url-${index}`}
          label={boUrls.length > 1 ? `BO URL ${index + 1}` : 'BO URL'}
          value={url}
          valueStyle={{ color: 'var(--primary)' }}
          onCopy={onCopy}
        />
      ))}
      {fields.map(field => (
        <CompactCredentialItem key={field.key} label={field.key} value={field.value || '-'} onCopy={onCopy} />
      ))}
      {remarkValues.map((remark, index) => (
        <CompactCredentialItem
          key={`remarks-${index}`}
          label={remarkValues.length > 1 ? `Remarks ${index + 1}` : 'Remarks'}
          value={remark}
          valueStyle={{ whiteSpace: 'pre-wrap' }}
          full
        />
      ))}
    </div>
  );
};

const ProviderMetaPill = ({ label, value, children = null }) => (
  <div style={{
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.35rem',
    minWidth: 0,
    padding: '0.3rem 0.45rem',
    border: '1px solid var(--border-light)',
    borderRadius: '6px',
    backgroundColor: 'var(--bg-card)'
  }}>
    <span style={{ color: 'var(--text-muted)', fontSize: '0.7rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
      {label}
    </span>
    <span style={{ display: 'inline-flex', alignItems: 'center', minWidth: 0 }}>
      {children || (
        <strong style={{ minWidth: 0, color: 'var(--text-primary)', fontSize: '0.76rem', wordBreak: 'break-word' }}>
          {value || '-'}
        </strong>
      )}
    </span>
  </div>
);

const TimezoneIndicatorCell = ({ row }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', minWidth: 0 }}>
    <ProviderMetaPill label="Timezone" value={row.provider_timezone || '-'} />
    <ProviderMetaPill label="Company Win Indicator">
      <span style={indicatorChipStyle(row.reporting_company_win_indicator)}>
        {formatWinIndicator(row.reporting_company_win_indicator)}
      </span>
    </ProviderMetaPill>
    <ProviderMetaPill label="Player Win Indicator">
      <span style={indicatorChipStyle(row.reporting_player_win_indicator)}>
        {formatWinIndicator(row.reporting_player_win_indicator)}
      </span>
    </ProviderMetaPill>
  </div>
);

const ProviderInfoSection = ({ title, children }) => (
  <div style={{
    display: 'grid',
    gridTemplateColumns: '96px minmax(0, 1fr)',
    gap: '0.55rem',
    alignItems: 'start',
    minWidth: 0,
    padding: '0.55rem 0.6rem',
    border: '1px solid var(--border-light)',
    borderRadius: '6px',
    backgroundColor: 'var(--bg-soft)'
  }}>
    <div style={{
      color: 'var(--text-secondary)',
      fontSize: '0.7rem',
      fontWeight: 800,
      letterSpacing: 0,
      textTransform: 'uppercase'
    }}>
      {title}
    </div>
    <div style={{ minWidth: 0 }}>
      {children}
    </div>
  </div>
);

const VerificationBadge = ({ verifiedAt, startDate = '', endDate = '' }) => {
  const verified = !!verifiedAt;
  const dataRangeLabel = startDate || endDate
    ? `${formatDate(startDate)} to ${formatDate(endDate)}`
    : 'not recorded';
  return (
    <span
      title={verified ? `Verified on ${formatDate(verifiedAt)}` : 'This provider row has not been verified with Get Data'}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.4rem',
        minHeight: '38px',
        padding: '0.35rem 0.6rem',
        borderRadius: '6px',
        border: verified ? '1px solid rgba(16, 185, 129, 0.35)' : '1px solid rgba(239, 68, 68, 0.35)',
        backgroundColor: verified ? 'rgba(16, 185, 129, 0.14)' : 'rgba(239, 68, 68, 0.12)',
        color: verified ? 'var(--success)' : 'var(--error)',
        fontSize: '0.74rem',
        fontWeight: 900,
        width: '100%',
        boxSizing: 'border-box',
        boxShadow: '0 1px 2px rgba(var(--neutral-rgb), 0.08)'
      }}
    >
      {verified ? <CheckCircleIcon size={15} /> : <AlertIcon size={15} />}
      <span style={{ display: 'grid', gap: '0.02rem', lineHeight: 1.1 }}>
        <span>{verified ? 'Verified' : 'Unverified'}</span>
        {verified && (
          <span style={{ fontSize: '0.66rem', fontWeight: 800, color: 'var(--text-secondary)' }}>
            Data: {dataRangeLabel}
          </span>
        )}
      </span>
    </span>
  );
};

const ProviderDetailsCell = ({ row, fetching, canFetchReport, fetchDisabledReason, onFetchReport, onCopy }) => (
  <div style={{ display: 'grid', gap: '0.5rem', minWidth: '430px' }}>
    <div style={{
      display: canFetchReport ? 'grid' : 'block',
      gridTemplateColumns: canFetchReport ? 'minmax(0, 1fr) auto' : undefined,
      gap: '0.75rem',
      alignItems: 'start',
      minWidth: 0
    }}>
      <div style={{ display: 'grid', gap: '0.25rem', minWidth: 0 }}>
        <div style={{ fontWeight: 800, color: 'var(--text-primary)', wordBreak: 'break-word' }}>
          {[row.provider_name, row.provider_category_name || row.provider_category_code].filter(Boolean).join(' - ') || '-'}
        </div>
        <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem', fontWeight: 700, wordBreak: 'break-word' }}>
          {getProviderCodeLabel(row)}
        </div>
        {isInactiveProvider(row) && (
          <span style={{ color: 'var(--error)', fontSize: '0.7rem', fontWeight: 800 }}>Inactive</span>
        )}
        {row.backend_code && (
          <div style={{ color: 'var(--text-muted)', fontSize: '0.72rem', fontWeight: 700, wordBreak: 'break-word' }}>
            Backend: {row.backend_code}
          </div>
        )}
      </div>
      {canFetchReport && (
        <div style={{ display: 'grid', gap: '0.45rem', justifyItems: 'end' }}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => onFetchReport(row)}
            disabled={fetching}
            title={fetchDisabledReason || 'Fetch BO report data'}
            style={{ padding: '0.4rem 0.55rem', fontSize: '0.74rem', gap: '0.35rem', whiteSpace: 'nowrap' }}
          >
            <UploadIcon size={13} />
            {fetching ? 'Getting...' : 'Get Data'}
          </button>
        </div>
      )}
    </div>
    <ProviderInfoSection title="Reporting">
      <TimezoneIndicatorCell row={row} />
    </ProviderInfoSection>
    <ProviderInfoSection title="BO Credentials">
      <BoCredentialsCell
        credentials={row.bo_credentials}
        providerBoLink={row.provider_bo_link}
        providerRemarks={row.provider_remarks}
        onCopy={onCopy}
      />
    </ProviderInfoSection>
  </div>
);

const VarianceAmountInput = ({ label, value, onChange, disabled }) => (
  <label style={{ display: 'grid', gridTemplateColumns: '94px minmax(140px, 1fr)', alignItems: 'center', gap: '0.55rem', minWidth: 0 }}>
    <span style={{ color: 'var(--text-muted)', fontSize: '0.72rem', fontWeight: 700 }}>{label}</span>
    <input
      type="number"
      step="0.01"
      className="input"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      disabled={disabled}
      placeholder="0.00"
      style={{ width: '100%', minWidth: 0 }}
    />
  </label>
);

const VarianceValueRow = ({ label, value, valueStyle = {}, compact = false }) => (
  <div style={{
    display: 'grid',
    gridTemplateColumns: compact ? 'minmax(0, 1fr) minmax(0, auto)' : '120px minmax(140px, 1fr)',
    alignItems: 'center',
    gap: '0.55rem',
    padding: compact ? '0.55rem 0.6rem' : '0.55rem 0.65rem',
    border: '1px solid var(--border-light)',
    borderRadius: '6px',
    backgroundColor: 'var(--bg-soft)',
    minWidth: 0
  }}>
    <span style={{ minWidth: 0, color: 'var(--text-muted)', fontSize: '0.72rem', fontWeight: 700 }}>{label}</span>
    <strong style={{ minWidth: 0, color: 'var(--text-primary)', fontSize: '0.82rem', textAlign: 'right', overflowWrap: 'anywhere', ...valueStyle }}>
      {value || '-'}
    </strong>
  </div>
);

const WinLoseSection = ({ title, children }) => (
  <div style={{
    display: 'grid',
    gap: '0.5rem',
    padding: '0.65rem',
    border: '1px solid var(--border-light)',
    borderRadius: '6px',
    backgroundColor: 'var(--bg-soft)',
    minWidth: 0
  }}>
    <div style={{ color: 'var(--text-primary)', fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase' }}>
      {title}
    </div>
    <div style={{ display: 'grid', gap: '0.45rem', minWidth: 0 }}>
      {children}
    </div>
  </div>
);

const WinLoseMetricRow = ({ label, value, valueStyle = {} }) => (
  <div style={{
    display: 'grid',
    gridTemplateColumns: '120px minmax(140px, 1fr)',
    alignItems: 'center',
    gap: '0.55rem',
    minWidth: 0
  }}>
    <span style={{ minWidth: 0, color: 'var(--text-muted)', fontSize: '0.72rem', fontWeight: 700 }}>{label}</span>
    <strong style={{ minWidth: 0, color: 'var(--text-primary)', fontSize: '0.82rem', textAlign: 'right', overflowWrap: 'anywhere', ...valueStyle }}>
      {value || '-'}
    </strong>
  </div>
);

const BoWinLoseSection = ({ row = null, turnover, validTurnover, winlose, currencyCode = '' }) => (
  <WinLoseSection title="BO">
    {row && (
      <VerificationBadge
        verifiedAt={row.verified_at}
        startDate={row.bo_report_start_date}
        endDate={row.bo_report_end_date}
      />
    )}
    <WinLoseMetricRow label="Turnover" value={formatAmount(turnover, currencyCode)} />
    <WinLoseMetricRow label="Valid Turnover" value={formatAmount(validTurnover, currencyCode)} />
    <WinLoseMetricRow label="Player W/L" value={formatAmount(winlose, currencyCode)} valueStyle={{ fontWeight: 800 }} />
  </WinLoseSection>
);

const VarianceCell = ({ row, variance, disabled, onAmountChange }) => (
  <div style={{ display: 'grid', gap: '0.55rem', minWidth: '260px' }}>
    <BoWinLoseSection
      row={row}
      turnover={row.bo_turnover_amount}
      validTurnover={row.bo_valid_turnover_amount}
      winlose={row.bo_winlose_amount}
      currencyCode={row.currency_code}
    />
    <VarianceAmountInput
      label="Provider W/L"
      value={row.provider_winlose_amount}
      onChange={(value) => onAmountChange(row.master_provider_id, 'provider_winlose_amount', value)}
      disabled={disabled}
    />
    <VarianceValueRow
      label="Difference"
      value={formatAmount(variance, row.currency_code)}
      valueStyle={{ fontWeight: 800, ...getVarianceStyle(variance) }}
    />
  </div>
);

const VarianceTotal = ({ totals }) => (
  <div style={{ display: 'grid', gap: '0.45rem', minWidth: '260px' }}>
    <BoWinLoseSection
      turnover={totals.boTurnover}
      validTurnover={totals.boValidTurnover}
      winlose={totals.bo}
    />
    <VarianceValueRow label="Provider W/L" value={formatAmount(totals.provider)} valueStyle={{ fontWeight: 800 }} />
    <VarianceValueRow label="Difference" value={formatAmount(totals.variance)} valueStyle={{ fontWeight: 800, ...getVarianceStyle(totals.variance) }} />
  </div>
);

const RatesCompanyCell = ({ row, rateMargin, companyWinlose, companyWinloseAfterMargin }) => (
  <div style={{ display: 'grid', gap: '0.65rem', minWidth: '220px' }}>
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(3, minmax(58px, 1fr))',
      gap: '0.45rem',
      padding: '0.6rem',
      border: '1px solid var(--border-light)',
      borderRadius: '6px',
      backgroundColor: 'var(--bg-soft)',
      minWidth: 0
    }}>
      <StackedLabelValue label="Selling" value={formatRate(row.selling_rate)} />
      <StackedLabelValue label="Costing" value={formatRate(row.costing_rate)} />
      <StackedLabelValue label="Margin" value={formatRate(rateMargin)} valueStyle={getVarianceStyle(rateMargin)} />
    </div>
    <VarianceValueRow
      label="Client Win/Lose"
      value={formatAmount(companyWinlose, row.currency_code)}
      valueStyle={{ fontWeight: 800, ...getVarianceStyle(companyWinlose) }}
      compact
    />
    <VarianceValueRow
      label="Company W/L After Margin"
      value={formatAmount(companyWinloseAfterMargin, row.currency_code)}
      valueStyle={{ fontWeight: 800, ...getVarianceStyle(companyWinloseAfterMargin) }}
      compact
    />
  </div>
);

const NotesCell = ({ row, disabled, onNotesChange }) => (
  <label style={{ display: 'grid', gap: '0.35rem', minWidth: '260px' }}>
    <span style={{ color: 'var(--text-muted)', fontSize: '0.72rem', fontWeight: 700 }}>Notes</span>
    <textarea
      className="input"
      value={row.notes}
      onChange={(event) => onNotesChange(row.master_provider_id, 'notes', event.target.value)}
      disabled={disabled}
      placeholder="Optional"
      rows={4}
      style={{ width: '100%', minWidth: 0, minHeight: '96px', resize: 'vertical', lineHeight: 1.45 }}
    />
  </label>
);

const RatesCompanyTotal = ({ totals }) => (
  <div style={{ display: 'grid', gap: '0.45rem', minWidth: '220px' }}>
    <VarianceValueRow
      label="Client Win/Lose"
      value={formatAmount(totals.company)}
      valueStyle={{ fontWeight: 800, ...getVarianceStyle(totals.company) }}
      compact
    />
    <VarianceValueRow
      label="Company W/L After Margin"
      value={formatAmount(totals.companyAfterMargin)}
      valueStyle={{ fontWeight: 800, ...getVarianceStyle(totals.companyAfterMargin) }}
      compact
    />
  </div>
);

const getRateMargin = (row) => Number(row.selling_rate || 0) - Number(row.costing_rate || 0);

const getCompanyWinloseBase = (row) => {
  const boAmount = toAmountNumber(row.bo_winlose_amount);
  return boAmount * -1;
};

const getCompanyWinlose = (row) => (
  getCompanyWinloseBase(row)
);

const getCompanyWinloseAfterMargin = (row) => (
  (getRateMargin(row) / 100) * getCompanyWinloseBase(row)
);

const getProviderWinloseInBoDirection = (row) => {
  const providerAmount = toAmountNumber(row.provider_winlose_amount);
  return row.reporting_company_win_indicator === 'positive'
    ? providerAmount * -1
    : providerAmount;
};

const getVarianceAmount = (row) => (
  getProviderWinloseInBoDirection(row) - toAmountNumber(row.bo_winlose_amount)
);

const getProviderCodeLabel = (row) => row.master_provider_code || '-';

const formatProviderLabel = (row) => {
  const parts = [
    row.provider_name,
    row.provider_category_name || row.provider_category_code,
    row.master_provider_code
  ].filter(Boolean);
  return parts.join(' / ') || '-';
};

const isInactiveProvider = (row) => row.status === 'inactive' || (!row.status && row.is_active === false);

const normalizeRow = (row) => ({
  ...row,
  bo_turnover_amount: row.bo_turnover_amount === null || row.bo_turnover_amount === undefined
    ? ''
    : String(row.bo_turnover_amount),
  bo_valid_turnover_amount: row.bo_valid_turnover_amount === null || row.bo_valid_turnover_amount === undefined
    ? ''
    : String(row.bo_valid_turnover_amount),
  bo_winlose_amount: row.bo_winlose_amount === null || row.bo_winlose_amount === undefined
    ? ''
    : String(row.bo_winlose_amount),
  provider_winlose_amount: row.provider_winlose_amount === null || row.provider_winlose_amount === undefined
    ? ''
    : String(row.provider_winlose_amount),
  verified_at: row.verified_at || null,
  bo_report_start_date: row.bo_report_start_date || '',
  bo_report_end_date: row.bo_report_end_date || '',
  notes: row.notes || ''
});

const FinanceReconciliationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { closeCurrentTabAndNavigate } = useWorkspaceTabNavigation();
  const { updateWorkspaceTab } = useWorkspaceTabs();
  const appDialog = useAppDialog();
  const { admin, hasPermission, getDefaultRoute } = useAuth();
  const canView = hasPermission('finance_reconciliation', 'view');
  const canExport = canView && hasPermission('finance_reconciliation', 'export');
  const canCreate = hasPermission('finance_reconciliation', 'create');
  const canEdit = hasPermission('finance_reconciliation', 'edit');
  const canSave = canCreate || canEdit;
  const canApproveReport = admin?.role === 'super_admin' || admin?.role === 'finance';
  const canViewFinancial = admin?.role === 'finance' ||
    hasPermission('finance_reconciliation', 'view_financial') ||
    hasPermission('finance_reconciliation', 'edit_financial');

  const [report, setReport] = useState(null);
  const [providerSearch, setProviderSearch] = useState('');
  const [providerStatusFilter, setProviderStatusFilter] = useState('all');
  const [verificationFilter, setVerificationFilter] = useState('all');
  const [differenceFilter, setDifferenceFilter] = useState('all');
  const [reportStartDate, setReportStartDate] = useState('');
  const [reportEndDate, setReportEndDate] = useState('');
  const [reportEnvironment, setReportEnvironment] = useState('production');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [metaSaving, setMetaSaving] = useState(false);
  const [isMetaModalOpen, setIsMetaModalOpen] = useState(false);
  const [metaForm, setMetaForm] = useState(createReportMetaForm(null));
  const [metaFormError, setMetaFormError] = useState('');
  const [workflowSaving, setWorkflowSaving] = useState('');
  const [fetchingProviderId, setFetchingProviderId] = useState('');
  const [baselineRows, setBaselineRows] = useState([]);
  const activeReportIdRef = useRef(null);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const currentWorkspacePath = `${location.pathname}${location.search}${location.hash}`;

  useEffect(() => {
    activeReportIdRef.current = id;
    return () => {
      activeReportIdRef.current = null;
    };
  }, [id]);

  const fetchRows = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await financeReconciliationService.getReportRows(id);
      if (activeReportIdRef.current !== id) return;
      if (res.success) {
        const nextRows = (res.data?.rows || []).map(normalizeRow);
        setReport(res.data?.report || null);
        setRows(nextRows);
        setBaselineRows(nextRows);
      } else {
        setError(res.message || 'Failed to fetch report detail');
      }
    } finally {
      if (activeReportIdRef.current === id) setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!canView) {
      navigate(getDefaultRoute(), { replace: true });
    }
  }, [canView, getDefaultRoute, navigate]);

  useEffect(() => {
    if (canView) fetchRows();
  }, [canView, fetchRows]);

  useEffect(() => {
    if (!report) return;
    const defaultStartDate = getMonthStartDate(report.report_month) || getDateInputValue(report.report_date);
    const defaultEndDate = getMonthEndDate(report.report_month) || getDateInputValue(report.report_date) || defaultStartDate;

    setReportStartDate(prev => prev || defaultStartDate);
    setReportEndDate(prev => prev || defaultEndDate);
  }, [report]);

  const reportStatus = report?.is_deleted ? 'deleted' : (report?.status || 'draft');
  const reportIsEditable = EDITABLE_REPORT_STATUSES.includes(reportStatus);
  const canModifyReport = !!report && reportIsEditable;
  const canSubmitAsCurrentAdmin = admin?.role === 'super_admin' || isSameObjectId(report?.created_by, admin);
  const canSaveReportRows = canSave && canModifyReport;
  const canEditReportMeta = canEdit && !!report && !report.is_deleted;
  const showSubmitReportButton = canSave && canModifyReport;
  const canSubmitReport = showSubmitReportButton && canSubmitAsCurrentAdmin;
  const submitDisabledReason = !canSubmitAsCurrentAdmin
    ? 'Only the report creator or Super Admin can submit to Finance'
    : '';
  const canDecideReport = canApproveReport && reportStatus === 'submitted';
  const detailTableColumnCount = canViewFinancial ? 4 : 3;
  const detailTableMinWidth = canViewFinancial ? '1380px' : '1080px';
  const handleRowsSaved = useCallback((res, automatic) => {
    notifyFinanceReconciliationUpdated();
    if (!automatic) {
      setSuccessMessage(`Saved ${res.saved_count || 0} provider rows`);
      setTimeout(() => setSuccessMessage(''), 2500);
    }
  }, []);

  const { saving, saveRows, autoSaveFailed, hasUnsavedChanges } = useFinanceReconciliationAutoSave({
    id, rows, setRows, baselineRows, setBaselineRows, canCreate, canEdit,
    enabled: canSaveReportRows,
    blocked: loading || metaSaving || !!workflowSaving || !!fetchingProviderId,
    saveReportRows: financeReconciliationService.saveReportRows,
    onSaved: handleRowsSaved,
    onError: setError,
    getRowLabel: formatProviderLabel
  });

  useEffect(() => {
    updateWorkspaceTab(currentWorkspacePath, { isDirty: hasUnsavedChanges });
    return () => updateWorkspaceTab(currentWorkspacePath, { isDirty: false });
  }, [currentWorkspacePath, hasUnsavedChanges, updateWorkspaceTab]);

  useEffect(() => {
    if (!hasUnsavedChanges) return undefined;

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
      return '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const confirmLeaveWithUnsavedChanges = useCallback(async () => {
    if (!hasUnsavedChanges) return true;

    return appDialog.confirm({
      title: 'Discard unsaved changes?',
      message: 'You have unsaved finance reconciliation changes. Leave this page and discard them?',
      confirmLabel: 'Discard',
      cancelLabel: 'Stay',
      variant: 'danger'
    });
  }, [appDialog, hasUnsavedChanges]);

  const handleBack = useCallback(async () => {
    const confirmed = await confirmLeaveWithUnsavedChanges();
    if (!confirmed) return;

    updateWorkspaceTab(currentWorkspacePath, { isDirty: false });
    closeCurrentTabAndNavigate('/finance-reconciliation');
  }, [closeCurrentTabAndNavigate, confirmLeaveWithUnsavedChanges, currentWorkspacePath, updateWorkspaceTab]);

  const openMetaModal = () => {
    if (!canEditReportMeta) {
      setError('This finance report cannot be edited');
      return;
    }
    setMetaForm(createReportMetaForm(report));
    setMetaFormError('');
    setIsMetaModalOpen(true);
  };

  const closeMetaModal = () => {
    if (metaSaving) return;
    setIsMetaModalOpen(false);
    setMetaFormError('');
  };

  const handleMetaFormChange = (event) => {
    const { name, value, checked, type } = event.target;
    setMetaForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSaveMeta = async (event) => {
    event.preventDefault();

    if (!canEditReportMeta) {
      setMetaFormError('This finance report cannot be edited');
      return;
    }
    if (reportIsEditable && !metaForm.report_month) {
      setMetaFormError('Report month is required');
      return;
    }
    if (hasUnsavedChanges) {
      setMetaFormError('Save provider row changes before editing report details');
      return;
    }

    setMetaSaving(true);
    setMetaFormError('');
    const res = await financeReconciliationService.updateReport(id, {
      ...(reportIsEditable ? {
        title: metaForm.title,
        report_month: metaForm.report_month,
        notes: metaForm.notes
      } : {}),
      include_ownline: metaForm.include_ownline,
      include_free_wallet: metaForm.include_free_wallet
    });
    setMetaSaving(false);

    if (res.success) {
      setReportStartDate(getMonthStartDate(res.data?.report_month) || reportStartDate);
      setReportEndDate(getMonthEndDate(res.data?.report_month) || reportEndDate);
      setIsMetaModalOpen(false);
      setSuccessMessage('Report updated');
      notifyFinanceReconciliationUpdated();
      await fetchRows();
      setTimeout(() => setSuccessMessage(''), 2500);
    } else {
      setMetaFormError(res.message || 'Failed to update report');
    }
  };

  const filteredRows = useMemo(() => {
    const keyword = providerSearch.trim().toLowerCase();

    return rows.filter(row => (
      (providerStatusFilter === 'all' ||
        (providerStatusFilter === 'exclude_inactive' && !isInactiveProvider(row)) ||
        (providerStatusFilter === 'inactive' && isInactiveProvider(row))) &&
      (verificationFilter === 'all' ||
        (verificationFilter === 'verified' && isRowVerified(row)) ||
        (verificationFilter === 'unverified' && !isRowVerified(row))) &&
      (differenceFilter === 'all' ||
        (differenceFilter === 'zero' && isZeroDifference(row)) ||
        (differenceFilter === 'non_zero' && !isZeroDifference(row))) &&
      (!keyword ||
        formatProviderLabel(row).toLowerCase().includes(keyword) ||
        String(row.master_provider_code || '').toLowerCase().includes(keyword) ||
        String(row.backend_code || '').toLowerCase().includes(keyword))
    ));
  }, [differenceFilter, providerSearch, providerStatusFilter, rows, verificationFilter]);
  const hasActiveProviderFilters = !!providerSearch.trim() || providerStatusFilter !== 'all' ||
    verificationFilter !== 'all' || differenceFilter !== 'all';
  const clearProviderFilters = () => {
    setProviderSearch('');
    setProviderStatusFilter('all');
    setVerificationFilter('all');
    setDifferenceFilter('all');
  };

  const handleExport = () => {
    if (!canExport || !report || loading || filteredRows.length === 0) return;

    try {
      exportFinanceReport(report, filteredRows, canViewFinancial);
    } catch {
      setError('Failed to export finance report. Please try again.');
    }
  };

  const getTotals = useCallback((sourceRows) => sourceRows.reduce((nextTotals, row) => {
    const boTurnoverAmount = toAmountNumber(row.bo_turnover_amount);
    const boValidTurnoverAmount = toAmountNumber(row.bo_valid_turnover_amount);
    const boAmount = toAmountNumber(row.bo_winlose_amount);
    const providerAmount = toAmountNumber(row.provider_winlose_amount);
    return {
      boTurnover: nextTotals.boTurnover + boTurnoverAmount,
      boValidTurnover: nextTotals.boValidTurnover + boValidTurnoverAmount,
      bo: nextTotals.bo + boAmount,
      provider: nextTotals.provider + providerAmount,
      variance: nextTotals.variance + getVarianceAmount(row),
      company: nextTotals.company + getCompanyWinlose(row),
      companyAfterMargin: nextTotals.companyAfterMargin + getCompanyWinloseAfterMargin(row),
      saved: nextTotals.saved + (row.has_report ? 1 : 0)
    };
  }, { boTurnover: 0, boValidTurnover: 0, bo: 0, provider: 0, variance: 0, company: 0, companyAfterMargin: 0, saved: 0 }), []);

  const totals = useMemo(() => getTotals(rows), [getTotals, rows]);
  const visibleTotals = useMemo(() => getTotals(filteredRows), [filteredRows, getTotals]);
  const verifiedCount = useMemo(() => rows.filter(isRowVerified).length, [rows]);

  const updateRowFields = (masterProviderId, fields) => {
    setRows(prevRows => prevRows.map(row => (
      String(row.master_provider_id) === String(masterProviderId)
        ? { ...row, ...fields }
        : row
    )));
  };

  const updateRow = (masterProviderId, field, value) => {
    updateRowFields(masterProviderId, { [field]: value });
  };

  const getProviderFetchDisabledReason = (row) => {
    if (!canSave) return 'Create or edit permission is required to fetch BO data';
    if (report?.is_deleted) return 'Deleted finance reports cannot fetch BO data';
    if (!reportIsEditable) return 'Submitted or approved finance reports cannot fetch BO data';
    if (saving || workflowSaving) return 'Please wait for the current action to finish';
    if (fetchingProviderId) return 'Another provider data request is running';
    if (!reportStartDate || !reportEndDate) return 'Start date and end date are required';
    if (reportStartDate > reportEndDate) return 'Start date cannot be after end date';
    if (!row.backend_code) return 'Provider backend code is required';
    if (!row.provider_timezone) return 'Provider timezone is required';
    return '';
  };

  const handleFetchProviderData = async (row) => {
    const disabledReason = getProviderFetchDisabledReason(row);
    if (disabledReason) {
      setError(disabledReason);
      return;
    }

    setFetchingProviderId(row.master_provider_id);
    setError(null);

    try {
      const res = await financeReconciliationService.fetchProviderReport(id, {
        master_provider_id: row.master_provider_id,
        environment: reportEnvironment,
        start_date: reportStartDate,
        end_date: reportEndDate,
        timezone: row.provider_timezone,
        is_free_wallet: row.isFreeCredit ? 1 : 0
      });

      if (!res.success) {
        setError(res.message || 'Failed to fetch BO data');
        return;
      }

      const amount = res.data?.bo_winlose_amount;
      if (amount === null || amount === undefined || !Number.isFinite(Number(amount))) {
        setError('BO data was fetched, but no win/loss amount was found in the response');
        return;
      }

      const fetchedFields = {
        row_id: res.data?.row_id || row.row_id || null,
        has_report: res.data?.has_report ?? true,
        bo_turnover_amount: String(res.data?.bo_turnover_amount ?? 0),
        bo_valid_turnover_amount: String(res.data?.bo_valid_turnover_amount ?? 0),
        bo_winlose_amount: String(amount),
        verified_at: res.data?.verified_at || new Date().toISOString(),
        bo_report_start_date: res.data?.bo_report_start_date || reportStartDate,
        bo_report_end_date: res.data?.bo_report_end_date || reportEndDate
      };

      updateRowFields(row.master_provider_id, fetchedFields);
      setBaselineRows(currentRows => currentRows.map(currentRow => (
        String(currentRow.master_provider_id) === String(row.master_provider_id)
          ? { ...currentRow, ...fetchedFields }
          : currentRow
      )));

      setSuccessMessage(`Fetched and verified ${row.backend_code || row.master_provider_code} BO data`);
      notifyFinanceReconciliationUpdated();
      setTimeout(() => setSuccessMessage(''), 2500);
    } catch (err) {
      setError(err.message || 'Failed to fetch BO data');
    } finally {
      setFetchingProviderId('');
    }
  };

  const handleCopyValue = async (value, label) => {
    const text = String(value || '').trim();
    if (!text) return;

    try {
      await navigator.clipboard.writeText(text);
      setSuccessMessage(`${label} copied`);
      setTimeout(() => setSuccessMessage(''), 1800);
    } catch {
      setError(`Failed to copy ${label}`);
    }
  };

  const isRowDisabled = (row) => (
    loading || metaSaving || (saving && !canEdit) || !!workflowSaving || !reportIsEditable || (row.has_report ? !canEdit : !canCreate)
  );

  const handleSubmitReport = async () => {
    if (!canSubmitAsCurrentAdmin) {
      setError('Only the report creator or Super Admin can submit this finance report');
      return;
    }
    if (hasUnsavedChanges) {
      setError('Save provider rows and report settings before submitting this finance report');
      return;
    }
    if (totals.saved === 0) {
      setError('Save provider rows before submitting this finance report');
      return;
    }
    const confirmed = await appDialog.confirm({
      title: 'Submit finance report',
      message: 'Submit this finance report to Finance?',
      confirmLabel: 'Submit',
      details: [
        { label: 'Merchant', value: report?.merchant_id?.name || report?.merchant_name || '-' },
        { label: 'Month', value: report?.report_month || '-' },
        { label: 'Provider Win/Lose', value: formatAmount(totals.provider) },
        ...(canViewFinancial ? [
          { label: 'Company Win/Lose', value: formatAmount(totals.company) },
          { label: 'Company W/L After Margin', value: formatAmount(totals.companyAfterMargin) }
        ] : [])
      ],
      note: 'After submit, this report cannot be edited unless Finance rejects it.'
    });
    if (!confirmed) return;

    setWorkflowSaving('submit');
    setError(null);

    const res = await financeReconciliationService.submitReport(id);
    setWorkflowSaving('');

    if (res.success) {
      setReport(res.data || report);
      setSuccessMessage('Report submitted to Finance');
      notifyFinanceReconciliationUpdated();
      fetchRows();
      setTimeout(() => setSuccessMessage(''), 2500);
    } else {
      setError(res.message || 'Failed to submit finance report');
    }
  };

  const handleDecision = async (decision) => {
    if (hasUnsavedChanges) {
      setError('Save provider rows and report settings before reviewing this finance report');
      return;
    }

    const isReject = decision === 'reject';
    let note = '';

    if (isReject) {
      const rejectionNote = await appDialog.prompt({
        title: 'Reject finance report',
        message: 'Reject this finance report?',
        confirmLabel: 'Reject',
        variant: 'danger',
        inputLabel: 'Reject reason (optional)',
        inputPlaceholder: 'Add a reason for the report history',
        details: [
          { label: 'Merchant', value: report?.merchant_id?.name || report?.merchant_name || '-' },
          { label: 'Month', value: report?.report_month || '-' }
        ]
      });
      if (rejectionNote === null) return;
      note = rejectionNote;
    } else {
      const confirmed = await appDialog.confirm({
        title: 'Approve finance report',
        message: 'Approve this finance report?',
        confirmLabel: 'Approve',
        details: [
          { label: 'Merchant', value: report?.merchant_id?.name || report?.merchant_name || '-' },
          { label: 'Month', value: report?.report_month || '-' },
          { label: 'Provider Win/Lose', value: formatAmount(totals.provider) },
          ...(canViewFinancial ? [
            { label: 'Company Win/Lose', value: formatAmount(totals.company) },
            { label: 'Company W/L After Margin', value: formatAmount(totals.companyAfterMargin) }
          ] : [])
        ]
      });

      if (!confirmed) return;
    }

    setWorkflowSaving(decision);
    setError(null);

    const res = await financeReconciliationService.decideReport(id, { decision, note });
    setWorkflowSaving('');

    if (res.success) {
      setReport(res.data || report);
      setSuccessMessage(isReject ? 'Report rejected' : 'Report approved');
      notifyFinanceReconciliationUpdated();
      fetchRows();
      setTimeout(() => setSuccessMessage(''), 2500);
    } else {
      setError(res.message || 'Failed to update finance report approval');
    }
  };

  if (loading && !report) {
    return <div className="page-container" style={{ padding: '2rem', textAlign: 'center' }}>Loading report...</div>;
  }

  return (
    <div className="finance-reconciliation-detail-page" style={{ padding: '1rem' }}>
      <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
        <button className="btn" onClick={handleBack} style={{ width: '38px', height: '38px', padding: 0 }}>
          <ArrowLeftIcon />
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {canExport && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleExport}
              disabled={!report || loading || filteredRows.length === 0}
              title="Export currently filtered providers to Excel"
            >
              Export
            </button>
          )}
          {canSaveReportRows && (
            <>
              <span role="status" style={{ fontSize: '0.8rem', color: autoSaveFailed ? 'var(--error)' : 'var(--text-muted)' }}>
                {saving ? 'Saving...' : autoSaveFailed ? 'Save failed · Use Save All to retry' : hasUnsavedChanges ? 'Unsaved changes · Auto-save pending' : 'All changes saved'}
              </span>
              <button className="btn btn-primary" onClick={() => saveRows()} disabled={saving || loading || metaSaving || !!workflowSaving || !!fetchingProviderId}>
                Save All
              </button>
            </>
          )}
          {showSubmitReportButton && (
            <button
              className="btn btn-secondary"
              onClick={handleSubmitReport}
              disabled={saving || loading || !!workflowSaving || totals.saved === 0 || !canSubmitReport}
              title={submitDisabledReason || undefined}
            >
              {workflowSaving === 'submit' ? 'Submitting...' : 'Submit to Finance'}
            </button>
          )}
          {canDecideReport && (
            <>
              <button className="btn btn-primary" onClick={() => handleDecision('approve')} disabled={saving || loading || !!workflowSaving}>
                {workflowSaving === 'approve' ? 'Approving...' : 'Approve'}
              </button>
              <button className="btn btn-secondary" onClick={() => handleDecision('reject')} disabled={saving || loading || !!workflowSaving}>
                {workflowSaving === 'reject' ? 'Rejecting...' : 'Reject'}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="card finance-report-overview">
        <div className="finance-report-overview__header">
          <div className="finance-report-overview__identity">
            <span className="finance-report-overview__eyebrow">Report Details</span>
            <strong className="finance-report-overview__name">{report?.title || report?._id || '-'}</strong>
            <span className="finance-report-overview__merchant">
              {report?.merchant_id?.name || report?.merchant_name || '-'}
              {(report?.merchant_id?.merchant_code || report?.merchant_code) &&
                ` · ${report?.merchant_id?.merchant_code || report?.merchant_code}`}
            </span>
          </div>
          <div className="finance-report-overview__actions">
            <span className="finance-report-overview__status" style={getStatusStyle(reportStatus)}>
              {STATUS_LABELS[reportStatus] || reportStatus}
            </span>
            {canEditReportMeta && (
              <button type="button" className="btn btn-secondary finance-report-overview__button" onClick={openMetaModal} disabled={metaSaving || saving || loading || !!workflowSaving}>
                <EditIcon size={14} /> Edit
              </button>
            )}
          </div>
        </div>

        <div className="finance-report-overview__meta">
          <span><b>Month</b> {report?.report_month || '-'}</span>
          <span><b>Creator</b> {formatAdminName(report?.created_by)}</span>
          {report?.submitted_at && <span><b>Submitted</b> {formatAdminName(report?.submitted_by)} · {formatDate(report.submitted_at)}</span>}
          {report?.decided_at && <span><b>{STATUS_LABELS[reportStatus] || 'Reviewed'}</b> {formatAdminName(report?.decided_by)} · {formatDate(report.decided_at)}</span>}
          {report?.deleted_at && <span><b>Deleted</b> {formatAdminName(report?.deleted_by)} · {formatDate(report.deleted_at)}</span>}
          {report?.decision_note && <span><b>Note</b> {report.decision_note}</span>}
        </div>

        <div className="finance-report-overview__settings">
          <span className="finance-report-overview__eyebrow">Report Settings</span>
          <span>Ownline <b>{report?.include_ownline === false ? 'Excluded' : 'Included'}</b></span>
          <span>Free Wallet <b>{report?.include_free_wallet === true ? 'Included' : 'Excluded'}</b></span>
        </div>
      </div>

      <div style={summaryGridStyle}>
        <div className="card" style={summaryCardStyle}>
          <div style={summaryLabelStyle}>Providers</div>
          <div style={summaryCountValueStyle}>{rows.length}</div>
        </div>
        <div className="card" style={summaryCardStyle}>
          <div style={summaryLabelStyle}>Saved Rows</div>
          <div style={summaryCountValueStyle}>{totals.saved}</div>
        </div>
        <div className="card" style={summaryCardStyle}>
          <div style={summaryLabelStyle}>Verified Rows</div>
          <div style={summaryCountValueStyle}>{verifiedCount}</div>
        </div>
        <div className="card" style={summaryCardStyle}>
          <div style={summaryLabelStyle}>Provider Win/Lose</div>
          <div style={summaryValueStyle}>{formatAmount(totals.provider)}</div>
        </div>
        {canViewFinancial && (
          <div className="card" style={summaryCardStyle}>
            <div style={summaryLabelStyle}>Company Win/Lose</div>
            <div style={{ ...summaryValueStyle, ...getVarianceStyle(totals.company) }}>{formatAmount(totals.company)}</div>
          </div>
        )}
        {canViewFinancial && (
          <div className="card" style={summaryCardStyle}>
            <div style={summaryLabelStyle}>Company W/L After Margin</div>
            <div style={{ ...summaryValueStyle, ...getVarianceStyle(totals.companyAfterMargin) }}>{formatAmount(totals.companyAfterMargin)}</div>
          </div>
        )}
      </div>

      <div className="card finance-report-controls">
        <div className="finance-report-controls__filters-section">
          <div className="finance-report-controls__heading">
            <div>
              <div className="finance-report-controls__title">Provider Filters</div>
              <div className="finance-report-controls__caption">Showing {filteredRows.length} of {rows.length} providers</div>
            </div>
            {hasActiveProviderFilters && (
              <button type="button" className="finance-report-controls__clear" onClick={clearProviderFilters}>
                Clear filters
              </button>
            )}
          </div>
          <div className="finance-report-controls__filter-grid">
            <div className="finance-report-controls__field">
              <label htmlFor="finance-provider-search" style={labelStyle}>Provider Search</label>
              <div className="search-container">
                <div className="search-icon-wrapper"><SearchIcon /></div>
                <input
                  id="finance-provider-search"
                  type="text"
                  className="search-input"
                  placeholder="Search name, category, or code"
                  value={providerSearch}
                  onChange={(event) => setProviderSearch(event.target.value)}
                />
              </div>
            </div>
            <div className="finance-report-controls__field">
              <label htmlFor="finance-provider-status" style={labelStyle}>Provider Status</label>
              <select id="finance-provider-status" className="input" value={providerStatusFilter} onChange={(event) => setProviderStatusFilter(event.target.value)}>
                <option value="all">All providers</option>
                <option value="exclude_inactive">Filter out Inactive</option>
                <option value="inactive">Inactive only</option>
              </select>
            </div>
            <div className="finance-report-controls__field">
              <label htmlFor="finance-verification" style={labelStyle}>Verification</label>
              <select id="finance-verification" className="input" value={verificationFilter} onChange={(event) => setVerificationFilter(event.target.value)}>
                {Object.entries(VERIFICATION_FILTER_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <div className="finance-report-controls__field">
              <label htmlFor="finance-difference" style={labelStyle}>Difference</label>
              <select id="finance-difference" className="input" value={differenceFilter} onChange={(event) => setDifferenceFilter(event.target.value)}>
                {Object.entries(DIFFERENCE_FILTER_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className="finance-report-controls__data-section">
          <div className="finance-report-controls__data-heading">
            <span className="finance-report-controls__title">Get Data Options</span>
            <span className="finance-report-controls__caption">Used when fetching provider BO data</span>
          </div>
          <div className="finance-report-controls__data-grid">
            <div className="finance-report-controls__field">
              <label htmlFor="finance-start-date" style={labelStyle}>Start Date</label>
              <input id="finance-start-date" type="date" className="input" value={reportStartDate} disabled={!canModifyReport} onChange={(event) => setReportStartDate(event.target.value)} />
            </div>
            <div className="finance-report-controls__field">
              <label htmlFor="finance-end-date" style={labelStyle}>End Date</label>
              <input id="finance-end-date" type="date" className="input" value={reportEndDate} disabled={!canModifyReport} onChange={(event) => setReportEndDate(event.target.value)} />
            </div>
            <div className="finance-report-controls__field">
              <label htmlFor="finance-environment" style={labelStyle}>Environment</label>
              <select id="finance-environment" className="input" value={reportEnvironment} disabled={!canModifyReport} onChange={(event) => setReportEnvironment(event.target.value)}>
                <option value="production">Production</option>
                <option value="staging">Staging</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <FloatingBannerStack
        error={error}
        success={successMessage}
        onDismissError={() => setError(null)}
        onDismissSuccess={() => setSuccessMessage('')}
      />

      <div className="card" style={{ overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table className="table-striped" style={{ width: '100%', minWidth: detailTableMinWidth, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: 'var(--table-header-bg)', borderBottom: '1px solid var(--border-light)' }}>
                <th style={tableHeaderStyle}>Provider</th>
                {canViewFinancial && (
                  <th style={{ ...tableHeaderStyle, ...detailColumnStyle }}>Client Win/Lose</th>
                )}
                <th style={{ ...tableHeaderStyle, ...detailColumnStyle }}>Win / Lose</th>
                <th style={{ ...tableHeaderStyle, ...detailColumnStyle }}>Notes</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={detailTableColumnCount} style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>No providers found.</td>
                </tr>
              ) : filteredRows.map(row => {
                const variance = getVarianceAmount(row);
                const rateMargin = getRateMargin(row);
                const companyWinlose = getCompanyWinlose(row);
                const companyWinloseAfterMargin = getCompanyWinloseAfterMargin(row);
                const disabled = isRowDisabled(row);
                const fetchDisabledReason = getProviderFetchDisabledReason(row);

                return (
                  <tr key={row.master_provider_id}>
                    <td style={tableCellStyle}>
                      <ProviderDetailsCell
                        row={row}
                        fetching={String(fetchingProviderId) === String(row.master_provider_id)}
                        canFetchReport={canSave && canModifyReport}
                        fetchDisabledReason={fetchDisabledReason}
                        onFetchReport={handleFetchProviderData}
                        onCopy={handleCopyValue}
                      />
                    </td>
                    {canViewFinancial && (
                      <td style={{ ...tableCellStyle, ...detailColumnStyle }}>
                        <RatesCompanyCell
                          row={row}
                          rateMargin={rateMargin}
                          companyWinlose={companyWinlose}
                          companyWinloseAfterMargin={companyWinloseAfterMargin}
                        />
                      </td>
                    )}
                    <td style={{ ...tableCellStyle, ...detailColumnStyle }}>
                      <VarianceCell row={row} variance={variance} disabled={disabled} onAmountChange={updateRow} />
                    </td>
                    <td style={{ ...tableCellStyle, ...detailColumnStyle }}>
                      <NotesCell row={row} disabled={disabled} onNotesChange={updateRow} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {filteredRows.length > 0 && (
              <tfoot>
                <tr style={{ backgroundColor: 'var(--bg-soft)', borderTop: '1px solid var(--border-light)' }}>
                  <td style={{ ...tableCellStyle, fontWeight: 800 }}>Total</td>
                  {canViewFinancial && (
                    <td style={{ ...tableCellStyle, ...detailColumnStyle }}>
                      <RatesCompanyTotal totals={visibleTotals} />
                    </td>
                  )}
                  <td style={{ ...tableCellStyle, ...detailColumnStyle }}>
                    <VarianceTotal totals={visibleTotals} />
                  </td>
                  <td style={{ ...tableCellStyle, ...detailColumnStyle }} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      <Modal isOpen={isMetaModalOpen} onClose={closeMetaModal} title="Edit Finance Report" maxWidth="680px">
        <form onSubmit={handleSaveMeta}>
          {metaFormError && (
            <div style={{ padding: '0.85rem', marginBottom: '1rem', border: '1px solid var(--error)', borderRadius: 'var(--radius-md)', color: 'var(--error)', backgroundColor: 'rgba(239, 68, 68, 0.08)' }}>
              {metaFormError}
            </div>
          )}
          {!reportIsEditable && (
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.8125rem', marginBottom: '1rem' }}>
              Report details are locked after submission. Report settings can still be changed.
            </p>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={labelStyle}>Month *</label>
              <input
                type="month"
                name="report_month"
                className="input"
                value={metaForm.report_month}
                onChange={handleMetaFormChange}
                required={reportIsEditable}
                disabled={!reportIsEditable}
              />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Title</label>
              <input
                type="text"
                name="title"
                className="input"
                value={metaForm.title}
                onChange={handleMetaFormChange}
                placeholder="Optional"
                disabled={!reportIsEditable}
              />
            </div>
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>Notes</label>
            <textarea
              name="notes"
              className="input"
              value={metaForm.notes}
              onChange={handleMetaFormChange}
              rows={3}
              style={{ resize: 'vertical' }}
              disabled={!reportIsEditable}
            />
          </div>
          <fieldset style={{ border: '1px solid var(--border-light)', borderRadius: 'var(--radius-md)', padding: '0.85rem 1rem', marginBottom: '1rem' }}>
            <legend style={{ padding: '0 0.35rem', fontSize: '0.8125rem', fontWeight: 700, color: 'var(--text-secondary)' }}>Report Settings</legend>
            <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap' }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.45rem', fontSize: '0.8125rem', fontWeight: 600 }}>
                <input type="checkbox" name="include_ownline" checked={metaForm.include_ownline} onChange={handleMetaFormChange} />
                Include Ownline
              </label>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.45rem', fontSize: '0.8125rem', fontWeight: 600 }}>
                <input type="checkbox" name="include_free_wallet" checked={metaForm.include_free_wallet} onChange={handleMetaFormChange} />
                Include Free Wallet
              </label>
            </div>
          </fieldset>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
            <button type="button" className="btn btn-secondary" onClick={closeMetaModal} disabled={metaSaving}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={metaSaving}>{metaSaving ? 'Saving...' : 'Save Changes'}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default FinanceReconciliationDetail;
